import { Button, Checkbox, Input, Select, message, Tag, Table } from "antd";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { Option } = Select;
const { TextArea } = Input;

const FORM_TYPE_COLOR = {
  transfer_certificate: "blue",
  study_certificate: "green",
  conduct_certificate: "purple",
};

const SECTION_TYPES = [
  { value: "header", label: "Header Block" },
  { value: "numbered_qa", label: "Standard Template" },
  { value: "declaration", label: "Declaration" },
  { value: "signature", label: "Signature Row" },
  { value: "custom_text", label: "Custom Text" },
];

const SECTION_LABEL_MAP = {
  header: "Header Block",
  numbered_qa: "Standard Template",
  declaration: "Declaration",
  signature: "Signature Row",
  custom_text: "Custom Text",
  fields_table: "Fields Table",
};

const MERGE_TAG_HINTS = [
  "{{student_name}}", "{{admission_no}}", "{{parent_name}}", "{{school_name}}",
  "{{from_class}}", "{{to_class}}", "{{from_year}}", "{{to_year}}",
  "{{conduct}}", "{{place}}", "{{issue_date}}", "{{file_number}}",
  "{{dob}}", "{{principal_name}}", "{{affiliation_no}}",
];

const DECLARATION_DEFAULTS = {
  study_certificate:
    "This is to certify that Mr./Ms. {{student_name}}, S/o / D/o {{parent_name}}, Admission No. {{admission_no}}, has studied in {{school_name}} from Class {{from_class}} to Class {{to_class}} during the academic years {{from_year}} to {{to_year}}.\n\nThis certificate is issued on the request of the student for official purposes.",
  conduct_certificate:
    "This is to certify that Mr./Ms. {{student_name}}, S/o / D/o {{parent_name}}, Admission No. {{admission_no}}, studied in {{school_name}} from Class {{from_class}} to Class {{to_class}} during the academic years {{from_year}} to {{to_year}}.\n\nDuring the period of his/her study in this institution, his/her conduct and character were found to be {{conduct}}.\n\nThis certificate is issued on the request of the student for official purposes.",
};

const FONTS = ["Roboto", "MavenPro", "Helvetica"];
const BORDER_STYLES = ["none", "single", "double"];

const defaultBranding = { primary_color: "#1a237e", border_style: "double", font: "Roboto" };

function defaultSectionConfig(type, formType) {
  switch (type) {
    case "header":
      return { show_school_name: true, show_affiliation: true, title: "TRANSFER CERTIFICATE" };
    case "fields_table":
      return { fields: [] };
    case "declaration":
      return { text: DECLARATION_DEFAULTS[formType] || "This is to certify that the above particulars are true to the best of my knowledge." };
    case "signature":
      return { show_stamp: true, show_principal_name: true };
    case "custom_text":
      return { text: "" };
    case "numbered_qa":
      return { items: AP_TC_SEED };
    default:
      return {};
  }
}

const AP_TC_SEED = [
  { number: "1", question: "Name of the School, Place and District", field_name: "school_name", field_type: "text", sub_items: [
    { marker: "(a)", question: "Highest class in the School", field_name: "highest_class", field_type: "text" },
    { marker: "(b)", question: "If Non-Govt., whether recognized & details", field_name: "recognition_details", field_type: "text" },
  ]},
  { number: "2", question: "Name of the Pupil", field_name: "student_name", field_type: "text", sub_items: [] },
  { number: "3", question: "Name of the Parent or Guardian", field_name: "parent_name", field_type: "text", sub_items: [
    { marker: "(a)", question: "Name of Mother", field_name: "mother_name", field_type: "text" },
  ]},
  { number: "4", question: "Nationality and Religion", field_name: "nationality_religion", field_type: "text", sub_items: [] },
  { number: "5", question: "Caste (SC/ST/BC/OC)", field_name: "caste_category", field_type: "text", sub_items: [] },
  { number: "6", question: "Date of Birth", field_name: "dob", field_type: "date", sub_items: [
    { marker: "(words)", question: "Date of Birth in words", field_name: "dob_words", field_type: "text" },
  ]},
  { number: "7", question: "Class in which the pupil was studying at the time of leaving", field_name: "class_leaving", field_type: "text", sub_items: [
    { marker: "(a)(1)", question: "First Language", field_name: "first_language", field_type: "text" },
    { marker: "(a)(2)", question: "Second Language", field_name: "second_language", field_type: "text" },
    { marker: "(b)", question: "Course offered", field_name: "course_offered", field_type: "text" },
  ]},
  { number: "8", question: "Medium of Instruction", field_name: "medium_of_instruction", field_type: "text", sub_items: [] },
  { number: "9", question: "Date of admission or promotion to current class", field_name: "date_of_admission", field_type: "date", sub_items: [] },
  { number: "10", question: "Class in which first admitted in this school", field_name: "class_first_admitted", field_type: "text", sub_items: [] },
  { number: "11", question: "Whether the pupil has paid all fees due to the school?", field_name: "fees_paid", field_type: "yes_no", sub_items: [] },
  { number: "12", question: "Whether the pupil has qualified for promotion to next higher class?", field_name: "qualified_promotion", field_type: "yes_no", sub_items: [] },
  { number: "13", question: "Personal Marks of Identification", field_name: "", field_type: "text", sub_items: [
    { marker: "(i)", question: "First mark", field_name: "personal_mark_1", field_type: "text" },
    { marker: "(ii)", question: "Second mark", field_name: "personal_mark_2", field_type: "text" },
  ]},
  { number: "14", question: "Date on which the pupil actually left the school", field_name: "date_left", field_type: "date", sub_items: [] },
  { number: "15", question: "Conduct", field_name: "conduct", field_type: "text", sub_items: [] },
  { number: "16", question: "Total working days and days present in last class attended", field_name: "attendance", field_type: "text", sub_items: [] },
  { number: "17", question: "Date of application for Transfer Certificate", field_name: "date_tc_applied", field_type: "date", sub_items: [] },
  { number: "18", question: "Date of issue of Transfer Certificate", field_name: "date_tc", field_type: "date", sub_items: [] },
];

// ─── Live Preview ─────────────────────────────────────────────────────────────

function CertPreview({ sections, branding }) {
  const color = branding.primary_color || "#1a237e";
  const font = branding.font || "Roboto";
  const border = branding.border_style;

  const borderStyle =
    border === "double"
      ? `4px double ${color}`
      : border === "single"
      ? `2px solid ${color}`
      : "none";

  const sorted = [...sections].sort((a, b) => a.order - b.order);

  const sampleValues = {
    student_name: "Arjun Kumar",
    admission_no: "ADM-2019-001",
    dob: "15-Aug-2012",
    gender: "Male",
    class: "Grade 8 - A",
    from_class: "5",
    to_class: "10",
    academic_year: "2025-2026",
    from_year: "2015-16",
    to_year: "2025-26",
    parent_name: "Suresh Kumar",
    school_name: "Your School Name",
    affiliation_no: "1234567",
    file_number: "CB-TC-2025-0001",
    issue_date: "01-Jul-2026",
    principal_name: "Mrs. Priya Nair",
    conduct: "Good",
    address: "123, Main Street",
    district: "Chennai",
    date_of_joining: "June 2019",
    years_known: "5",
    place: "Chennai",
  };

  function resolveText(text) {
    return (text || "").replace(/\{\{(\w+)\}\}/g, (_, k) => sampleValues[k] ?? `{{${k}}}`);
  }

  return (
    <div
      style={{
        fontFamily: font,
        border: borderStyle,
        padding: "24px",
        minHeight: "400px",
        background: "#fff",
        fontSize: "11px",
        lineHeight: "1.6",
      }}
    >
      {sorted.map((section, i) => {
        if (section.type === "header") {
          const c = section.config || {};
          return (
            <div key={i} style={{ textAlign: "center", marginBottom: "12px" }}>
              {c.show_school_name && (
                <div style={{ fontSize: "15px", fontWeight: "bold", color }}>{sampleValues.school_name}</div>
              )}
              {c.show_affiliation && (
                <div style={{ fontSize: "10px", color: "#555" }}>Affiliation No: {sampleValues.affiliation_no}</div>
              )}
              <hr style={{ borderColor: color, margin: "6px 0" }} />
              {c.title && (
                <div style={{ fontSize: "13px", fontWeight: "bold", textDecoration: "underline", color, marginBottom: "6px" }}>
                  {c.title}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                <span><strong>File No:</strong> {sampleValues.file_number}</span>
                <span><strong>Date:</strong> {sampleValues.issue_date}</span>
              </div>
            </div>
          );
        }
        if (section.type === "fields_table") {
          const fields = section.config?.fields || [];
          if (!fields.length)
            return <div key={i} style={{ color: "#aaa", textAlign: "center", margin: "8px 0" }}>[Fields table — add fields in the config panel]</div>;
          return (
            <table key={i} style={{ width: "100%", borderCollapse: "collapse", margin: "8px 0" }}>
              <tbody>
                {fields.map((f, fi) => (
                  <tr key={fi} style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <td style={{ fontWeight: "bold", width: "35%", padding: "3px 0", color: "#333" }}>{f.label}</td>
                    <td style={{ padding: "3px 0" }}>{sampleValues[f.field_name] ?? f.field_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        if (section.type === "declaration") {
          const declText = resolveText(section.config?.text || "");
          const paragraphs = declText.split("\n\n");
          return (
            <div key={i} style={{ margin: "12px 0", fontSize: "11px" }}>
              {paragraphs.map((para, pi) => (
                <div key={pi} style={{ marginBottom: pi < paragraphs.length - 1 ? "10px" : 0 }}>
                  {para.split("\n").map((line, li) => (
                    <div key={li}>{line}</div>
                  ))}
                </div>
              ))}
            </div>
          );
        }
        if (section.type === "signature") {
          const c = section.config || {};
          return (
            <div key={i} style={{ textAlign: "right", marginTop: "24px" }}>
              <div style={{ display: "inline-block", textAlign: "center" }}>
                <div style={{ borderBottom: "1px solid #333", width: "120px", margin: "0 auto 4px" }} />
                {c.show_principal_name && <div style={{ fontSize: "10px" }}>{sampleValues.principal_name}</div>}
                <div style={{ fontSize: "10px", fontWeight: "bold" }}>Principal</div>
                {c.show_stamp && <div style={{ fontSize: "9px", color: "#999" }}>(School Stamp)</div>}
              </div>
            </div>
          );
        }
        if (section.type === "custom_text") {
          return (
            <p key={i} style={{ margin: "6px 0" }}>
              {resolveText(section.config?.text) || <span style={{ color: "#aaa" }}>[Custom text]</span>}
            </p>
          );
        }
        if (section.type === "numbered_qa") {
          const qaItems = section.config?.items || [];
          if (!qaItems.length) {
            return <div key={i} style={{ color: "#aaa", textAlign: "center", margin: "8px 0", fontSize: "10px" }}>[Numbered Q&A — add items or load AP TC template]</div>;
          }
          return (
            <table key={i} style={{ width: "100%", borderCollapse: "collapse", margin: "8px 0", fontSize: "9.5px" }}>
              <tbody>
                {qaItems.map((item, qi) => (
                  <React.Fragment key={qi}>
                    <tr>
                      <td style={{ width: 22, textAlign: "right", fontWeight: "bold", paddingRight: 4, verticalAlign: "top", color: "#333" }}>{item.number}.</td>
                      <td style={{ paddingRight: 6 }}>{item.question}</td>
                      <td style={{ borderBottom: "1px solid #aaa", width: "35%", color: "#999", paddingLeft: 4 }}>
                        {item.field_name ? `[${item.field_type || "text"}]` : ""}
                      </td>
                    </tr>
                    {(item.sub_items || []).map((sub, si) => (
                      <tr key={si}>
                        <td></td>
                        <td style={{ paddingLeft: 12, color: "#555", paddingRight: 6 }}>{sub.marker} {sub.question}</td>
                        <td style={{ borderBottom: "1px solid #ddd", color: "#999", paddingLeft: 4 }}>
                          {sub.field_name ? `[${sub.field_type || "text"}]` : ""}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          );
        }
        return null;
      })}
      {sorted.length === 0 && (
        <div style={{ color: "#aaa", textAlign: "center", paddingTop: "60px"}}>Add sections from the left panel</div>
      )}
    </div>
  );
}

// ─── Shared style helpers ────────────────────────────────────────────────────

const cfgLabel = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, display: "block" };
const cfgSection = { marginBottom: 18 };
const cfgDivider = { borderTop: "1px solid #f0f0f0", margin: "16px 0" };

// ─── Section Config Panels ────────────────────────────────────────────────────

function HeaderConfig({ config, onChange }) {
  // show_address / show_phone / show_email / show_website default to true (shown unless explicitly false)
  const val = (key, def = true) => config[key] === undefined ? def : config[key];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={cfgSection}>
        <span style={cfgLabel}>School Details</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Checkbox checked={val("show_school_name")} onChange={(e) => onChange({ ...config, show_school_name: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>School Name</span>
          </Checkbox>
          <Checkbox checked={val("show_affiliation")} onChange={(e) => onChange({ ...config, show_affiliation: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>Affiliation No</span>
          </Checkbox>
          <Checkbox checked={val("show_address")} onChange={(e) => onChange({ ...config, show_address: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>Address</span>
          </Checkbox>
          <Checkbox checked={val("show_phone")} onChange={(e) => onChange({ ...config, show_phone: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>Phone</span>
          </Checkbox>
          <Checkbox checked={val("show_email")} onChange={(e) => onChange({ ...config, show_email: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>Email</span>
          </Checkbox>
          <Checkbox checked={val("show_website")} onChange={(e) => onChange({ ...config, show_website: e.target.checked })}>
            <span style={{ fontSize: 13, color: "#374151" }}>Website</span>
          </Checkbox>
        </div>
      </div>
      <div style={cfgDivider} />
      <div style={cfgSection}>
        <span style={cfgLabel}>Certificate Title</span>
        <Input
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder="e.g. TRANSFER CERTIFICATE"
          style={{ fontSize: 13 }}
        />
      </div>
    </div>
  );
}

function FieldsTableConfig({ config, onChange }) {
  const [customKey, setCustomKey] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const fields = config.fields || [];
  const selectedKeys = fields.map((f) => f.field_name);

  function moveField(idx, dir) {
    const updated = [...fields];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= updated.length) return;
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    onChange({ ...config, fields: updated });
  }

  function removeField(fieldName) {
    onChange({ ...config, fields: fields.filter((f) => f.field_name !== fieldName) });
  }

  function addCustomField() {
    const key = customKey.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const label = customLabel.trim();
    if (!key || !label) return message.warning("Enter both a field key and a label");
    if (selectedKeys.includes(key)) return message.warning("A field with this key already exists");
    onChange({ ...config, fields: [...fields, { field_name: key, label, custom: true }] });
    setCustomKey("");
    setCustomLabel("");
    setShowCustom(false);
  }

  return (
    <div>

      {/* ── Ordered list of selected fields ── */}
      {fields.length > 0 && (
        <>
          <div style={cfgDivider} />
          <div style={cfgSection}>
            <span style={cfgLabel}>Table Order — reorder or remove</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {fields.map((f, i) => (
                <div key={f.field_name} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "#f9fafb", borderRadius: 8, padding: "6px 10px",
                  border: "1px solid #e5e7eb",
                }}>
                  <span style={{ fontSize: 12, color: "#374151", flex: 1, fontWeight: 500 }}>{f.label}</span>
                  <Button size="small" type="text" disabled={i === 0}
                    style={{ padding: "0 3px", height: 22, color: "#9ca3af" }}
                    onClick={() => moveField(i, -1)}>↑</Button>
                  <Button size="small" type="text" disabled={i === fields.length - 1}
                    style={{ padding: "0 3px", height: 22, color: "#9ca3af" }}
                    onClick={() => moveField(i, 1)}>↓</Button>
                  <Button size="small" type="text" danger
                    style={{ padding: "0 3px", height: 22 }}
                    onClick={() => removeField(f.field_name)}>✕</Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Custom field ── */}
      <div style={cfgDivider} />
      {!showCustom ? (
        <Button type="dashed" block style={{ fontSize: 12 }} onClick={() => setShowCustom(true)}>
          + Add Custom Field
        </Button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={cfgLabel}>Add Custom Field</span>
          <Input placeholder="Field key (e.g. caste)" value={customKey}
            onChange={(e) => setCustomKey(e.target.value)} style={{ fontSize: 12 }} />
          <Input placeholder="Label (e.g. Caste)" value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            onPressEnter={addCustomField} style={{ fontSize: 12 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <Button type="primary" style={{ background: "#f97316", borderColor: "#f97316", flex: 1 }}
              onClick={addCustomField}>Add</Button>
            <Button onClick={() => { setShowCustom(false); setCustomKey(""); setCustomLabel(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeclarationConfig({ config, onChange }) {
  return (
    <div>
      <div style={cfgSection}>
        <span style={cfgLabel}>Declaration Text</span>
        <TextArea
          rows={5}
          value={config.text || ""}
          onChange={(e) => onChange({ ...config, text: e.target.value })}
          placeholder="Use {{merge_tags}} for dynamic values."
          style={{ fontSize: 12, resize: "vertical" }}
        />
      </div>
      <div style={cfgDivider} />
      <div>
        <span style={cfgLabel}>Click to Insert Tag</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {MERGE_TAG_HINTS.map((t) => (
            <Tag key={t} style={{ cursor: "pointer", fontSize: 11, padding: "2px 7px", borderRadius: 4 }}
              onClick={() => onChange({ ...config, text: (config.text || "") + t })}>
              {t}
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignatureConfig({ config, onChange }) {
  return (
    <div style={cfgSection}>
      <span style={cfgLabel}>Visibility</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Checkbox checked={config.show_principal_name} onChange={(e) => onChange({ ...config, show_principal_name: e.target.checked })}>
          <span style={{ fontSize: 13, color: "#374151" }}>Show Principal Name</span>
        </Checkbox>
        <Checkbox checked={config.show_stamp} onChange={(e) => onChange({ ...config, show_stamp: e.target.checked })}>
          <span style={{ fontSize: 13, color: "#374151" }}>Show School Stamp placeholder</span>
        </Checkbox>
      </div>
    </div>
  );
}

function CustomTextConfig({ config, onChange }) {
  return (
    <div>
      <div style={cfgSection}>
        <span style={cfgLabel}>Paragraph Text</span>
        <TextArea
          rows={5}
          value={config.text || ""}
          onChange={(e) => onChange({ ...config, text: e.target.value })}
          placeholder="Use {{merge_tags}} for dynamic values."
          style={{ fontSize: 12, resize: "vertical" }}
        />
      </div>
      <div style={cfgDivider} />
      <div>
        <span style={cfgLabel}>Click to Insert Tag</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {MERGE_TAG_HINTS.map((t) => (
            <Tag key={t} style={{ cursor: "pointer", fontSize: 11, padding: "2px 7px", borderRadius: 4 }}
              onClick={() => onChange({ ...config, text: (config.text || "") + t })}>
              {t}
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
}

const QA_FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "yes_no", label: "Yes/No" },
  { value: "date", label: "Date" },
  { value: "multiline", label: "Multiline" },
];

function NumberedQAConfig({ config, onChange }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const items = config.items || [];

  function updateItem(idx, field, val) {
    const updated = items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    onChange({ ...config, items: updated });
  }

  function removeItem(idx) {
    onChange({ ...config, items: items.filter((_, i) => i !== idx) });
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  }

  function addItem() {
    const newItem = { number: String(items.length + 1), question: "", field_name: "", field_type: "text", sub_items: [] };
    onChange({ ...config, items: [...items, newItem] });
    setExpandedIdx(items.length);
  }

  function updateSubItem(itemIdx, subIdx, field, val) {
    const updated = items.map((it, i) => {
      if (i !== itemIdx) return it;
      return { ...it, sub_items: (it.sub_items || []).map((s, si) => (si === subIdx ? { ...s, [field]: val } : s)) };
    });
    onChange({ ...config, items: updated });
  }

  function addSubItem(itemIdx) {
    const updated = items.map((it, i) => {
      if (i !== itemIdx) return it;
      return { ...it, sub_items: [...(it.sub_items || []), { marker: "", question: "", field_name: "", field_type: "text" }] };
    });
    onChange({ ...config, items: updated });
  }

  function removeSubItem(itemIdx, subIdx) {
    const updated = items.map((it, i) => {
      if (i !== itemIdx) return it;
      return { ...it, sub_items: (it.sub_items || []).filter((_, si) => si !== subIdx) };
    });
    onChange({ ...config, items: updated });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 600, overflowY: "auto", paddingRight: 2 }}>
      {items.map((item, idx) => (
        <div key={idx} style={{
          border: expandedIdx === idx ? "1.5px solid #f97316" : "1.5px solid #e5e7eb",
          borderRadius: 10,
          background: "#fff",
          boxShadow: expandedIdx === idx ? "0 2px 8px rgba(249,115,22,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
          transition: "box-shadow 0.15s, border-color 0.15s",
        }}>
          {/* Summary row */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", cursor: "pointer", background: expandedIdx === idx ? "#fff8f0" : "transparent", borderRadius: expandedIdx === idx ? "10px 10px 0 0" : 10 }}
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
          >
            <span style={{ fontWeight: 700, color: "#f97316", fontSize: 13, width: 26, flexShrink: 0 }}>{item.number}.</span>
            <span style={{ flex: 1, fontSize: 13, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "1.4" }}>
              {item.question || <span style={{ color: "#9ca3af" }}>(no question)</span>}
            </span>
            <Tag style={{ fontSize: 10, padding: "1px 6px", margin: 0, borderRadius: 4, flexShrink: 0 }}>{item.field_type || "text"}</Tag>
            <span style={{ fontSize: 14, flexShrink: 0, opacity: expandedIdx === idx ? 1 : 0.45 }} title="Click to edit">✏️</span>
            <Button size="small" type="text" danger style={{ padding: "0 4px", height: 24, flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); removeItem(idx); }}>✕</Button>
          </div>

          {/* Expanded edit form */}
          {expandedIdx === idx && (
            <div style={{ padding: "10px 10px 12px", borderTop: "1px solid #f0f0f0", background: "#fafafa" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <Input
                  size="small"
                  style={{ width: 44, fontSize: 12 }}
                  value={item.number}
                  onChange={(e) => updateItem(idx, "number", e.target.value)}
                  placeholder="No."
                />
                <Input
                  size="small"
                  style={{ flex: 1, fontSize: 12 }}
                  value={item.question}
                  onChange={(e) => updateItem(idx, "question", e.target.value)}
                  placeholder="Question text"
                />
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <Input
                  size="small"
                  style={{ flex: 1, fontSize: 12 }}
                  value={item.field_name}
                  onChange={(e) => updateItem(idx, "field_name", e.target.value)}
                  placeholder="field_name"
                />
                <Select size="small" style={{ width: 90 }} value={item.field_type || "text"}
                  onChange={(v) => updateItem(idx, "field_type", v)} getPopupContainer={(t) => t.parentNode}>
                  {QA_FIELD_TYPES.map((t) => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                </Select>
              </div>

              {/* Sub-items */}
              {(item.sub_items || []).length > 0 && (
                <div style={{ borderLeft: "3px solid #fed7aa", paddingLeft: 8, marginBottom: 8 }}>
                  {(item.sub_items || []).map((sub, si) => (
                    <div key={si} style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 6, padding: 8, marginBottom: 6 }}>
                      <div style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                        <Input size="small" style={{ width: 54, fontSize: 12 }} value={sub.marker}
                          onChange={(e) => updateSubItem(idx, si, "marker", e.target.value)} placeholder="(a)" />
                        <Input size="small" style={{ flex: 1, fontSize: 12 }} value={sub.question}
                          onChange={(e) => updateSubItem(idx, si, "question", e.target.value)} placeholder="Sub-question" />
                        <Button size="small" type="text" danger style={{ padding: "0 4px", height: 24, flexShrink: 0 }}
                          onClick={() => removeSubItem(idx, si)}>✕</Button>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Input size="small" style={{ flex: 1, fontSize: 12 }} value={sub.field_name}
                          onChange={(e) => updateSubItem(idx, si, "field_name", e.target.value)} placeholder="field_name" />
                        <Select size="small" style={{ width: 90 }} value={sub.field_type || "text"}
                          onChange={(v) => updateSubItem(idx, si, "field_type", v)} getPopupContainer={(t) => t.parentNode}>
                          {QA_FIELD_TYPES.map((t) => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button size="small" type="dashed" onClick={() => addSubItem(idx)} block style={{ fontSize: 11, height: 28 }}>
                + Add Sub-item
              </Button>
            </div>
          )}
        </div>
      ))}

      <Button type="dashed" onClick={addItem} block style={{ marginTop: 4, height: 34, fontSize: 13 }}>
        + Add Item
      </Button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CertBuilderTemplateDesigner = () => {
  const user = useSelector(selectUser);
  const schoolId = user?.school?._id;
  const queryClient = useQueryClient();

  const [templateName, setTemplateName] = useState("");
  const [formType, setFormType] = useState(null);
  const [sections, setSections] = useState([]);
  const [branding, setBranding] = useState(defaultBranding);
  const [selectedSectionIdx, setSelectedSectionIdx] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const templatesQuery = useQuery(
    ["cert-builder-templates", schoolId],
    () =>
      axios
        .get(`${serverUrl}/cert-builder-templates?school=${schoolId}`, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        })
        .then((r) => r.data),
    { enabled: !!schoolId }
  );

  function loadTemplate(tmpl) {
    setPreviewTemplate(null);
    setEditingTemplateId(tmpl._id);
    setTemplateName(tmpl.name);
    setFormType(tmpl.form_type);
    setSections(tmpl.sections ? [...tmpl.sections].sort((a, b) => a.order - b.order) : []);
    setBranding(tmpl.branding || defaultBranding);
    setSelectedSectionIdx(null);
  }

  function resetForm() {
    setPreviewTemplate(null);
    setEditingTemplateId(null);
    setTemplateName("");
    setFormType(null);
    setSections([]);
    setBranding(defaultBranding);
    setSelectedSectionIdx(null);
  }

  // Auto-populate sections when cert type changes on a new (not editing) template
  useEffect(() => {
    if (editingTemplateId) return;
    if (formType === "transfer_certificate") {
      setSections([
        { type: "header", order: 0, config: { show_school_name: true, show_affiliation: true, title: "TRANSFER CERTIFICATE" } },
        { type: "numbered_qa", order: 1, config: { items: AP_TC_SEED } },
        { type: "signature", order: 2, config: { show_stamp: true, show_principal_name: true } },
      ]);
    } else if (formType === "study_certificate" || formType === "conduct_certificate") {
      const title = formType === "study_certificate" ? "STUDY CERTIFICATE" : "CONDUCT CERTIFICATE";
      setSections([
        { type: "header", order: 0, config: { show_school_name: true, show_affiliation: true, title } },
        { type: "declaration", order: 1, config: { text: DECLARATION_DEFAULTS[formType] } },
        { type: "signature", order: 2, config: { show_stamp: true, show_principal_name: true } },
      ]);
    } else {
      setSections([]);
    }
    setSelectedSectionIdx(null);
  }, [formType, editingTemplateId]);

  function addSection(type) {
    const newSection = { type, order: sections.length, config: defaultSectionConfig(type, formType) };
    setSections([...sections, newSection]);
    setSelectedSectionIdx(sections.length);
  }

  function removeSection(idx) {
    setSections(sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
    setSelectedSectionIdx(null);
  }

  function moveSection(idx, dir) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const updated = [...sections];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setSections(updated.map((s, i) => ({ ...s, order: i })));
    setSelectedSectionIdx(newIdx);
  }

  function updateSectionConfig(idx, config) {
    setSections(sections.map((s, i) => (i === idx ? { ...s, config } : s)));
  }

  async function save() {
    if (!formType) return message.error("Please select a certificate type");
    if (!templateName.trim()) return message.error("Please enter a template name");
    if (!sections.length) return message.error("Add at least one section");

    if (!editingTemplateId) {
      const existing = (templatesQuery.data?.result?.data || []).find(
        (t) => t.form_type === formType
      );
      if (existing) {
        return message.error(
          `A template for "${existing.form_type.replace(/_/g, " ")}" already exists ("${existing.name}"). Edit the existing template or delete it before creating a new one.`
        );
      }
    }

    setSaving(true);
    try {
      const payload = {
        school: schoolId,
        form_type: formType,
        name: templateName.trim(),
        sections: sections.map((s, i) => ({ ...s, order: i })),
        branding,
        status: "active",
      };
      if (editingTemplateId) {
        await axios.put(`${serverUrl}/cert-builder-templates/${editingTemplateId}`, payload, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        });
        message.success("Template updated");
      } else {
        await axios.post(`${serverUrl}/cert-builder-templates`, payload, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        });
        message.success("Template created");
      }
      queryClient.invalidateQueries(["cert-builder-templates", schoolId]);
      resetForm();
    } catch {
      message.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  }

  async function deleteTemplate(id) {
    try {
      await axios.delete(`${serverUrl}/cert-builder-templates/${id}`, {
        headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
      });
      message.success("Template deleted");
      queryClient.invalidateQueries(["cert-builder-templates", schoolId]);
      if (editingTemplateId === id) resetForm();
    } catch {
      message.error("Failed to delete template");
    }
  }

  const selectedSection = selectedSectionIdx !== null ? sections[selectedSectionIdx] : null;
  const savedTemplates = templatesQuery.data?.result?.data || [];

  return (
    <div className="mt-8">
      {/* Saved templates row */}
      <div
        style={{ borderRadius: "10px", color: "#616161", marginBottom: 16 }}
        className="bg-white drop-shadow-md p-6 mb-4"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl text-orange-500 font-semibold">Manage Templates</span>
        </div>
        <Table
          dataSource={savedTemplates}
          rowKey="_id"
          loading={templatesQuery.isLoading}
          pagination={false}
          size="middle"
          onRow={(r) => ({
            onClick: () => {
              if (!editingTemplateId) setPreviewTemplate(r);
            },
            style: { cursor: editingTemplateId ? "default" : "pointer" },
          })}
          rowClassName={(r) => {
            if (r._id === editingTemplateId) return "ant-table-row-selected";
            if (!editingTemplateId && r._id === previewTemplate?._id) return "ant-table-row-selected";
            return "";
          }}
          columns={[
            {
              title: "Template Name",
              dataIndex: "name",
              key: "name",
              render: (v, r) => (
                <span style={{ fontWeight: 600, color: r._id === editingTemplateId ? "#f97316" : "#1f2937" }}>{v}</span>
              ),
            },
            {
              title: "Certificate Type",
              dataIndex: "form_type",
              key: "form_type",
              render: (v) => <Tag color={FORM_TYPE_COLOR[v] || "default"} style={{ textTransform: "capitalize" }}>{v.replace(/_/g, " ")}</Tag>,
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (v) => <Tag color={v === "active" ? "green" : "default"}>{v}</Tag>,
            },
            {
              title: "Actions",
              key: "actions",
              align: "center",
              render: (_, r) => (
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <Button
                    size="small"
                    onClick={() => { resetForm(); setPreviewTemplate(r); }}
                    style={{ borderColor: "#6b7280", color: "#6b7280" }}
                  >
                    Preview
                  </Button>
                  <Button size="small" type="primary" ghost onClick={() => loadTemplate(r)}>Edit</Button>
                  <Button size="small" danger onClick={() => deleteTemplate(r._id)}>Delete</Button>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Template meta — hidden when all types are taken and not editing */}
      {(editingTemplateId || savedTemplates.length < 3) && (
      <div style={{ borderRadius: "10px", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", padding: "24px 28px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 20 }}>
          {editingTemplateId ? "Edit Template" : "Create New Template"}
        </div>

        {/* Type cards — hidden when editing (type is fixed) */}
        {!editingTemplateId && (
          <>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, marginBottom: 12 }}>
              Step 1 — Choose a certificate type
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
              {[
                { value: "transfer_certificate", label: "Transfer Certificate", icon: "📄", desc: "For students leaving the school" },
                { value: "study_certificate",    label: "Study Certificate",    icon: "📚", desc: "Proof of enrolment or study period" },
                { value: "conduct_certificate",  label: "Conduct Certificate",  icon: "🎖️", desc: "Character and behaviour record" },
              ].map((t) => {
                const active = formType === t.value;
                const taken = (templatesQuery.data?.result?.data || []).some(
                  (tmpl) => tmpl.form_type === t.value
                );
                return (
                  <div
                    key={t.value}
                    onClick={() => { if (!taken) { setFormType(t.value); setPreviewTemplate(null); } }}
                    style={{
                      flex: "1 1 180px",
                      border: active ? "2px solid #f97316" : taken ? "2px solid #d1d5db" : "2px solid #e5e7eb",
                      borderRadius: 12,
                      padding: "18px 20px",
                      cursor: taken ? "not-allowed" : "pointer",
                      background: active ? "#fff8f0" : taken ? "#f3f4f6" : "#fafafa",
                      opacity: taken ? 0.6 : 1,
                      transition: "border-color 0.15s, background 0.15s",
                      boxShadow: active ? "0 0 0 3px rgba(249,115,22,0.12)" : "none",
                    }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{t.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: active ? "#f97316" : taken ? "#9ca3af" : "#1f2937", marginBottom: 4 }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{t.desc}</div>
                    {taken && (
                      <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280", fontWeight: 700 }}>Template already exists</div>
                    )}
                    {active && !taken && (
                      <div style={{ marginTop: 10, fontSize: 11, color: "#f97316", fontWeight: 700 }}>✓ Selected</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Editing mode — show cert type as read-only badge */}
        {editingTemplateId && (
          <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Certificate Type:</span>
            <Tag color={FORM_TYPE_COLOR[formType] || "default"} style={{ fontSize: 13, padding: "2px 10px", textTransform: "capitalize" }}>
              {formType.replace(/_/g, " ")}
            </Tag>
          </div>
        )}

        {/* Name + Save — always visible */}
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, marginBottom: 10 }}>
          {editingTemplateId ? "Template Name" : "Step 2 — Name your template"}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Input
            size="large"
            style={{ flex: "1 1 280px", maxWidth: 400, fontSize: 14 }}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. Standard Transfer Certificate 2025"
          />
          <Button
            type="primary"
            size="large"
            onClick={save}
            loading={saving}
            style={{ background: "#f97316", borderColor: "#f97316", paddingLeft: 32, paddingRight: 32 }}
          >
            {editingTemplateId ? "Update Template" : "Save Template"}
          </Button>
          {(editingTemplateId || formType) && (
            <Button size="large" onClick={resetForm} disabled={saving}>
              Cancel
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Idle preview panel — only when no type is selected and not editing */}
      {!editingTemplateId && !formType && (
        <div style={{ borderRadius: 10, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", padding: "24px 28px", marginBottom: 16, minHeight: 280 }}>
          {previewTemplate ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#1f2937" }}>{previewTemplate.name}</span>
                  <Tag color={FORM_TYPE_COLOR[previewTemplate.form_type] || "default"} style={{ textTransform: "capitalize", fontSize: 12 }}>
                    {previewTemplate.form_type.replace(/_/g, " ")}
                  </Tag>
                  <Tag color={previewTemplate.status === "active" ? "green" : "default"}>{previewTemplate.status}</Tag>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={() => setPreviewTemplate(null)}>Close Preview</Button>
                  <Button type="primary" style={{ background: "#f97316", borderColor: "#f97316" }} onClick={() => loadTemplate(previewTemplate)}>
                    Edit This Template
                  </Button>
                </div>
              </div>
              <div style={{ maxWidth: 595, margin: "0 auto" }}>
                <CertPreview sections={previewTemplate.sections || []} branding={previewTemplate.branding || defaultBranding} />
              </div>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 220, color: "#9ca3af", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📄</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>No template selected</div>
              <div style={{ fontSize: 13 }}>Select a certificate type above, or click Preview on a saved template</div>
            </div>
          )}
        </div>
      )}

      {/* Main editor — 3 columns — shown while editing OR after a type card is selected in create mode */}
      {(editingTemplateId || formType) && <div className="flex gap-4">
        {/* Left: Section list + Branding */}
        <div style={{ borderRadius: "10px", width: 272, minWidth: 272, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", padding: "20px 16px", display: "flex", flexDirection: "column", gap: 0 }}>

          {/* Sections heading */}
          <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>
            {editingTemplateId ? "✏️ Editing Template" : "✨ Creating Template"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 10 }}>Sections</div>

          {sections.length === 0 && (
            <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>No sections yet.</div>
          )}
          {sections.map((s, i) => (
            <div
              key={i}
              onClick={() => setSelectedSectionIdx(i)}
              style={{
                borderRadius: 8,
                borderLeft: selectedSectionIdx === i ? "3px solid #f97316" : "3px solid transparent",
                background: selectedSectionIdx === i ? "#fff8f0" : "#f9fafb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 10px",
                marginBottom: 5,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: selectedSectionIdx === i ? "#f97316" : "#374151", textTransform: "capitalize" }}>
                {SECTION_LABEL_MAP[s.type] || s.type.replace(/_/g, " ")}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                <Button size="small" type="text" disabled={i === 0} style={{ padding: "0 4px", height: 24 }} onClick={(e) => { e.stopPropagation(); moveSection(i, -1); }}>↑</Button>
                <Button size="small" type="text" disabled={i === sections.length - 1} style={{ padding: "0 4px", height: 24 }} onClick={(e) => { e.stopPropagation(); moveSection(i, 1); }}>↓</Button>
                <Button size="small" type="text" danger style={{ padding: "0 4px", height: 24 }} onClick={(e) => { e.stopPropagation(); removeSection(i); }}>✕</Button>
              </div>
            </div>
          ))}

          <div style={{ borderTop: "1px solid #f0f0f0", margin: "14px 0 12px" }} />

          {/* Add Section */}
          <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 10 }}>Add Section</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {SECTION_TYPES.map((t) => (
              <Button key={t.value} block type="dashed" size="small" style={{ textAlign: "left", height: 32, fontSize: 13 }} onClick={() => addSection(t.value)}>
                + {t.label}
              </Button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #f0f0f0", margin: "16px 0 14px" }} />

          {/* Branding */}
          <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 14 }}>Branding</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Border Style</div>
              <Select value={branding.border_style} onChange={(v) => setBranding({ ...branding, border_style: v })} style={{ width: "100%" }} getPopupContainer={(t) => t.parentNode}>
                {BORDER_STYLES.map((b) => <Option key={b} value={b}>{b}</Option>)}
              </Select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Font</div>
              <Select value={branding.font} onChange={(v) => setBranding({ ...branding, font: v })} style={{ width: "100%" }} getPopupContainer={(t) => t.parentNode}>
                {FONTS.map((f) => <Option key={f} value={f}>{f}</Option>)}
              </Select>
            </div>
          </div>
        </div>

        {/* Middle: Config panel */}
        <div style={{ borderRadius: "10px", width: 300, minWidth: 300, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {selectedSection ? (
            <>
              <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff8f0" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#f97316", textTransform: "capitalize", letterSpacing: "0.02em" }}>
                  {SECTION_LABEL_MAP[selectedSection.type] || selectedSection.type.replace(/_/g, " ")}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Configure this section's content and options</div>
              </div>
              <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
                {selectedSection.type === "header" && <HeaderConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
                {selectedSection.type === "fields_table" && <FieldsTableConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
                {selectedSection.type === "numbered_qa" && <NumberedQAConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
                {selectedSection.type === "declaration" && <DeclarationConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
                {selectedSection.type === "signature" && <SignatureConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
                {selectedSection.type === "custom_text" && <CustomTextConfig config={selectedSection.config} onChange={(c) => updateSectionConfig(selectedSectionIdx, c)} />}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>☰</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No Section Selected</div>
              <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>Click a section on the left panel to configure its content and options.</div>
            </div>
          )}
        </div>

        {/* Right: Live preview */}
        <div
          style={{ borderRadius: "10px", color: "#616161", marginBottom: 16 }}
          className="flex-1 bg-white drop-shadow-md p-4"
        >
          <div className="text-base font-semibold text-gray-700 pb-3">Live Preview</div>
          <div style={{ maxWidth: 595, margin: "0 auto" }}>
            <CertPreview sections={sections} branding={branding} />
          </div>
        </div>
      </div>}
    </div>
  );
};

export default CertBuilderTemplateDesigner;
